import React, { useEffect, useState } from 'react';
import Select from '../../components/UI/Select/Select';
import { CategoryList } from '../../components/UI/Select/select.props';
import { useApi } from '../../hooks/useApi';
import { useStores } from '../../hooks/useStore';
import { CoursesModel } from '../../models/courses';

const ALL_COURSES_ID = '';
const ALL_LESSONS_ID = '';

export interface ReviewFiltersValue {
  course_id?: string;
  lesson_id?: string;
}

export interface ReviewFiltersProps {
  value: ReviewFiltersValue;
  onChange: (_v: ReviewFiltersValue) => void;
  disabled?: boolean;
}

const ReviewFilters: React.FC<ReviewFiltersProps> = ({ value, onChange, disabled = false }) => {
  const api = useApi();
  const { notificationsStore } = useStores();

  const [courses, setCourses] = useState<CoursesModel[]>([]);
  const [lessonsByCourse, setLessonsByCourse] = useState<Record<string, CategoryList[]>>({});

  useEffect(() => {
    let cancelled = false;

    const loadCourses = async () => {
      try {
        const resp = await api.courses.get(0, 1000);
        if (cancelled) {
          return;
        }
        if (!resp.success || !resp.body) {
          notificationsStore.setError('Ошибка при загрузке курсов');
          return;
        }
        setCourses(resp.body);
      } catch {
        if (!cancelled) {
          notificationsStore.setError('Ошибка при загрузке курсов');
        }
      }
    };

    loadCourses();

    return () => {
      cancelled = true;
    };
  }, []);

  const loadLessons = async (courseId: string) => {
    try {
      const modulesResp = await api.courses.getModules(courseId);
      if (!modulesResp.success || !modulesResp.body) {
        notificationsStore.setError('Ошибка при загрузке уроков');
        return;
      }
      const modules = modulesResp.body;
      const lessonsResults = await Promise.all(
        modules.map((courseModule) => api.courses.getLessons(courseModule.id)),
      );

      let hasError = false;
      const options: CategoryList[] = [];
      modules.forEach((courseModule, index) => {
        const lessonsResp = lessonsResults[index];
        if (!lessonsResp.success || !lessonsResp.body) {
          hasError = true;
          return;
        }
        lessonsResp.body.forEach((lesson) => {
          options.push({ id: lesson.id, name: `${courseModule.title} — ${lesson.title}` });
        });
      });

      if (hasError) {
        notificationsStore.setError('Ошибка при загрузке уроков');
      }

      setLessonsByCourse((prev) => ({ ...prev, [courseId]: options }));
    } catch {
      notificationsStore.setError('Ошибка при загрузке уроков');
    }
  };

  const handleCourseChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
    const courseId = e.currentTarget.value;

    if (!courseId) {
      onChange({});
      return;
    }

    onChange({ course_id: courseId });

    if (!lessonsByCourse[courseId]) {
      loadLessons(courseId);
    }
  };

  const handleLessonChange: React.ChangeEventHandler<HTMLSelectElement> = (e) => {
    const lessonId = e.currentTarget.value;

    if (!lessonId) {
      onChange({ course_id: value.course_id });
      return;
    }

    onChange({ course_id: value.course_id, lesson_id: lessonId });
  };

  const courseOptions: CategoryList[] = [
    { id: ALL_COURSES_ID, name: 'Все курсы' },
    ...courses
      .filter((course): course is CoursesModel & { id: string } => Boolean(course.id))
      .map((course) => ({ id: course.id, name: course.title ?? '' })),
  ];

  const lessonOptions: CategoryList[] = value.course_id
    ? [{ id: ALL_LESSONS_ID, name: 'Все уроки' }, ...(lessonsByCourse[value.course_id] ?? [])]
    : [];

  return (
    <fieldset disabled={disabled} style={{ border: 'none', margin: 0, padding: 0 }}>
      <Select
        aria-label="Курс"
        listCategories={courseOptions}
        selectedValue={value.course_id ?? ALL_COURSES_ID}
        onChange={handleCourseChange}
      />
      {value.course_id ? (
        <Select
          aria-label="Урок"
          listCategories={lessonOptions}
          selectedValue={value.lesson_id ?? ALL_LESSONS_ID}
          onChange={handleLessonChange}
        />
      ) : null}
    </fieldset>
  );
};

export { ReviewFilters };
export default ReviewFilters;
